import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "./firebase";
import {
  doc, getDoc, getDocs, collection, query,
  where, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "./App";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatBox({ label, value, accent }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 12, padding: "16px 20px", textAlign: "center", flex: 1, minWidth: 100,
    }}>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: accent || "white", letterSpacing: 1 }}>{value ?? "—"}</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", letterSpacing: 1, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Toggle({ label, description, value, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ flex: 1, marginRight: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "white" }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 3, lineHeight: 1.4 }}>{description}</div>}
      </div>
      <button onClick={() => onChange(!value)} style={{
        width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer",
        background: value ? "#FFD700" : "rgba(255,255,255,0.15)",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: "50%", background: value ? "#0a1628" : "white",
          position: "absolute", top: 3, left: value ? 25 : 3,
          transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }} />
      </button>
    </div>
  );
}

const EMAIL_PREF_KEYS = ["uePickReminders", "ueWeeklyRecap", "fpSalaryEmails", "fpLineupReminders", "fpWeeklyRecap"];

// A labeled block of one game's settings.
function GameGroup({ emoji, name, color, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
        <span>{emoji}</span>{name}
      </div>
      <div style={{ borderLeft: `2px solid ${color}55`, paddingLeft: 14, marginTop: 2 }}>{children}</div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{
      fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 2,
      color: "rgba(255,255,255,0.35)", marginBottom: 12, marginTop: 28,
    }}>
      {children}
    </div>
  );
}

// ─── Contest history row ──────────────────────────────────────────────────────

function HistoryRow({ game, contestName, entries, season, rank, totalPts, inProgress }) {
  const gameColor = game === "ue" ? "#FF6B35" : "#FFD700";
  const gameLabel = game === "ue" ? "🏈 Underdog Edge" : "⚽ World Cup Pool";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
      borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap",
    }}>
      <div style={{ minWidth: 120 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: gameColor, letterSpacing: 1 }}>{gameLabel}</span>
          {inProgress && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1,
              color: "#00C853", background: "rgba(0,200,83,0.12)",
              border: "1px solid rgba(0,200,83,0.3)",
              borderRadius: 4, padding: "1px 5px",
            }}>IN SEASON</span>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "white", marginTop: 2 }}>{contestName}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>{season}</div>
      </div>
      <div style={{ flex: 1, minWidth: 100 }}>
        {entries.map((e, i) => (
          <div key={i} style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
            {e}
          </div>
        ))}
      </div>
      {rank && (
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>RANK</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: gameColor }}>#{rank}</div>
        </div>
      )}
      {totalPts !== null && totalPts !== undefined && (
        <div style={{ textAlign: "right", minWidth: 60 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>PTS</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "white" }}>{totalPts}</div>
        </div>
      )}
    </div>
  );
}

// ─── Main AccountPage ─────────────────────────────────────────────────────────

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, displayName, setDisplayName, logout } = useAuth();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ games: 0, contests: 0, entries: 0, top5: 0 });

  // Edit display name
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(displayName || "");
  const [savingName, setSavingName] = useState(false);
  const [nameStatus, setNameStatus] = useState("");

  // Preferences
  // Email preferences (every key defaults to on unless explicitly false)
  const [prefs, setPrefs] = useState({ uePickReminders: true, ueWeeklyRecap: true, fpSalaryEmails: true, fpLineupReminders: true, fpWeeklyRecap: true });

  useEffect(() => {
    if (!user?.uid) return;
    loadAll();
  }, [user?.uid]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Load user doc
      const userSnap = await getDoc(doc(db, "users", user.uid));
      const ud = userSnap.data() || {};
      setUserData(ud);
      setPrefs(Object.fromEntries(EMAIL_PREF_KEYS.map((k) => [k, ud[k] !== false])));

      // Load UE history
      const ueContestsSnap = await getDocs(
        query(collection(db, "ue_contests"), where("members", "array-contains", user.uid))
      );
      const ueContests = ueContestsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const ueHistory = [];
      let totalUEEntries = 0;

      for (const c of ueContests) {
        const entriesSnap = await getDocs(
          query(collection(db, "ue_entries"),
            where("contestId", "==", c.id),
            where("ownerUid", "==", user.uid))
        );
        const myEntries = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        totalUEEntries += myEntries.length;

        // Get rank from full leaderboard
        const allEntriesSnap = await getDocs(
          query(collection(db, "ue_entries"), where("contestId", "==", c.id))
        );
        const allEntries = allEntriesSnap.docs.map((d) => d.data())
          .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));

        const myBestEntry = myEntries.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))[0];
        const myRank = myBestEntry
          ? allEntries.findIndex((e) => e.ownerUid === user.uid) + 1
          : null;
        const myPts = myBestEntry?.totalPoints || 0;

        const ueInSeason = (() => {
          const now = new Date();
          const seasonStart = new Date("2026-09-03T00:00:00Z");
          const seasonEnd = new Date("2027-01-07T00:00:00Z");
          return now >= seasonStart && now <= seasonEnd;
        })();

        ueHistory.push({
          game: "ue",
          contestName: c.name,
          season: c.season || "2026",
          entries: myEntries.map((e) => e.name),
          rank: myRank || null,
          totalPts: myPts,
          inProgress: ueInSeason,
        });
      }

      // Load WC Pool history
      const wcContestsSnap = await getDocs(
        query(collection(db, "contests"), where("members", "array-contains", user.uid))
      );
      const wcContests = wcContestsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const wcHistory = [];
      let totalWCEntries = 0;

      for (const c of wcContests) {
        const entriesSnap = await getDocs(
          query(collection(db, "entries"),
            where("contestId", "==", c.id),
            where("userId", "==", user.uid))
        );
        const myEntries = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        totalWCEntries += myEntries.length;

        const allEntriesSnap = await getDocs(
          query(collection(db, "entries"), where("contestId", "==", c.id))
        );
        const allEntries = allEntriesSnap.docs.map((d) => d.data())
          .sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));

        const myBestEntry = myEntries.sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0))[0];
        const myRank = myBestEntry
          ? allEntries.findIndex((e) => e.userId === user.uid) + 1
          : null;

        wcHistory.push({
          game: "wc",
          contestName: c.name,
          season: "2026",
          entries: myEntries.map((e) => e.entryName || e.userName),
          rank: myRank || null,
          totalPts: myBestEntry?.totalScore || 0,
          inProgress: false, // WC 2026 is complete
        });
      }

      const allHistory = [...ueHistory, ...wcHistory];
      setHistory(allHistory);

      const gamesPlayed = new Set([
        ...ueContests.length > 0 ? ["ue"] : [],
        ...wcContests.length > 0 ? ["wc"] : [],
      ]).size;

      setStats({
        games: gamesPlayed,
        contests: ueContests.length + wcContests.length,
        entries: totalUEEntries + totalWCEntries,
        // Finished pools (any game) where your best entry placed 1st-5th. Pools still in
        // season don't count until they're over.
        top5: allHistory.filter((h) => !h.inProgress && h.rank && h.rank <= 5).length,
      });
    } catch (e) { console.error("AccountPage load error:", e); }
    setLoading(false);
  };

  const saveName = async () => {
    if (!nameDraft.trim() || nameDraft.trim() === displayName) { setEditingName(false); return; }
    setSavingName(true);
    setNameStatus("");
    try {
      await updateDoc(doc(db, "users", user.uid), {
        displayName: nameDraft.trim(),
        updatedAt: serverTimestamp(),
      });
      setDisplayName(nameDraft.trim());
      setNameStatus("✓ Name updated");
      setEditingName(false);
      setTimeout(() => setNameStatus(""), 3000);
    } catch (e) { setNameStatus("Failed to save. Try again."); }
    setSavingName(false);
  };

  // One game's email setting. Turning any email on also clears the "all emails off" flag.
  const savePref = async (key, val) => {
    const prev = prefs;
    setPrefs({ ...prefs, [key]: val });
    try {
      await updateDoc(doc(db, "users", user.uid), val ? { [key]: true, emailAll: true } : { [key]: false });
    } catch (e) { console.error("Failed to save pref:", e); setPrefs(prev); }
  };

  // Master switch: flips every game's emails at once (and future games default to match).
  const setAllEmails = async (val) => {
    const prev = prefs;
    const next = Object.fromEntries(EMAIL_PREF_KEYS.map((k) => [k, val]));
    setPrefs(next);
    try {
      await updateDoc(doc(db, "users", user.uid), { ...next, emailAll: val });
    } catch (e) { console.error("Failed to save prefs:", e); setPrefs(prev); }
  };
  const anyEmailOn = EMAIL_PREF_KEYS.some((k) => prefs[k]);

  const formatDate = (ts) => {
    if (!ts) return "Unknown";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--navy)" }}>

      {/* Header */}
      <header style={{
        background: "linear-gradient(135deg, #060e1d 0%, #0a1628 100%)",
        borderBottom: "1px solid var(--border)",
        padding: "0 20px",
      }}>
        <div style={{ maxWidth: 700, margin: "0 auto", display: "flex", alignItems: "center", height: 60, gap: 12 }}>
          <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm">← Back</button>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 3, color: "white" }}>
            GANNI<span style={{ color: "#FFD700" }}>GAMES</span>
            <span style={{ color: "rgba(255,255,255,0.3)", marginLeft: 10, fontSize: 14 }}>/ My Account</span>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "28px 16px 60px" }}>

        {/* Profile header */}
        <div style={{
          background: "var(--navy-card)", border: "1px solid var(--border)",
          borderRadius: 16, padding: "24px", marginBottom: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: "linear-gradient(135deg, #FFD700, #FF6B35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#0a1628", flexShrink: 0,
            }}>
              {(displayName || "?").charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "white", letterSpacing: 1 }}>
                {displayName}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>
                {user?.email}
                {userData?.createdAt && (
                  <span style={{ marginLeft: 10 }}>· Member since {formatDate(userData.createdAt)}</span>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          {!loading && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <StatBox label="GAMES" value={stats.games} accent="#FFD700" />
              <StatBox label="CONTESTS" value={stats.contests} accent="#FFD700" />
              <StatBox label="ENTRIES" value={stats.entries} accent="#FFD700" />
              <StatBox label="TOP 5 FINISHES" value={stats.top5} accent="#FFD700" />
            </div>
          )}
        </div>

        {/* Display Name */}
        <SectionHeader>PROFILE</SectionHeader>
        <div className="card" style={{ padding: "20px 20px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 1, marginBottom: 10 }}>DISPLAY NAME</div>
          {editingName ? (
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                className="input" maxLength={30} autoFocus
              />
              <button onClick={saveName} disabled={savingName} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                {savingName ? "..." : "Save"}
              </button>
              <button onClick={() => { setEditingName(false); setNameDraft(displayName); }} className="btn btn-ghost btn-sm">✕</button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: "white" }}>{displayName}</span>
              <button onClick={() => setEditingName(true)} className="btn btn-ghost btn-sm">Edit</button>
            </div>
          )}
          {nameStatus && (
            <p style={{ fontSize: 12, color: nameStatus.startsWith("✓") ? "#00C853" : "#E53935", marginTop: 8 }}>{nameStatus}</p>
          )}
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", marginTop: 8 }}>
            Your display name appears on leaderboards and in pool rosters.
          </div>
        </div>

        {/* Preferences */}
        <SectionHeader>EMAIL PREFERENCES</SectionHeader>
        <div className="card" style={{ padding: "4px 20px 12px" }}>
          <Toggle
            label="All Emails"
            description="Turn every Ganni Games email on or off at once. You can still fine-tune each game below."
            value={anyEmailOn}
            onChange={setAllEmails}
          />

          <GameGroup emoji="🏈" name="Underdog Edge" color="#FF6B35">
            <Toggle
              label="Pick Reminders"
              description="Thursday morning and Saturday evening when you haven't picked yet."
              value={prefs.uePickReminders}
              onChange={(val) => savePref("uePickReminders", val)}
            />
            <Toggle
              label="Weekly Recap"
              description="Wednesday morning: last week's biggest upset, standings and your results."
              value={prefs.ueWeeklyRecap}
              onChange={(val) => savePref("ueWeeklyRecap", val)}
            />
          </GameGroup>

          <GameGroup emoji="🎖️" name="Franchise Player" color="#14B8A6">
            <Toggle
              label="Salaries Are Live"
              description="Wednesday 10am Pacific, when the week's prices are set and lineups open."
              value={prefs.fpSalaryEmails}
              onChange={(val) => savePref("fpSalaryEmails", val)}
            />
            <Toggle
              label="Lineup Reminders"
              description="Thursday 9am if you haven't set a lineup; Saturday noon and early Sunday if you still have open spots."
              value={prefs.fpLineupReminders}
              onChange={(val) => savePref("fpLineupReminders", val)}
            />
            <Toggle
              label="Weekly Recap"
              description="Tuesday morning: the week's winner, your score, your perfect lineup and the standings."
              value={prefs.fpWeeklyRecap}
              onChange={(val) => savePref("fpWeeklyRecap", val)}
            />
          </GameGroup>
        </div>

        {/* Game History */}
        <SectionHeader>GAME HISTORY</SectionHeader>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 24, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>Loading history...</div>
          ) : history.length === 0 ? (
            <div style={{ padding: 24, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
              No contest history yet — join a pool to get started!
            </div>
          ) : (
            history.map((h, i) => (
              <HistoryRow key={i} {...h} />
            ))
          )}
        </div>

        {/* Sign out */}
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <button onClick={() => { logout(); navigate("/"); }} className="btn btn-ghost btn-sm" style={{ color: "#E53935", borderColor: "rgba(229,57,53,0.3)" }}>
            Sign Out
          </button>
        </div>

      </div>
    </div>
  );
}
